import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  onSnapshot,
  getDocs,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  updateDoc,
  Timestamp,
  documentId
} from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { PilgrimGuideLink } from '../types/pilgrimGuide';
import { normalizePassportNumber } from '../utils/passportUtils';
import { linkPilgrimToGuideAction, linkWaitingIdentificationAction } from './pilgrimGuide/actions/linkActions';
import { deleteLinkAction } from './pilgrimGuide/actions/deleteActions';
import { useLanguage } from '../contexts/LanguageContext';
import { useBansStore } from './bansStore';
import { useSuspensionStore } from './suspensionStore';
import toast from 'react-hot-toast';

// Constants
const BATCH_SIZE = 500; // Firestore batch limit is 500
const FIREBASE_IN_LIMIT = 30; // Firebase 'in' operator limit
const DEFAULT_PAGE_SIZE = 500; // Increased from 100 to 500 to load more data at once

type PilgrimGuideState = {
  links: Record<string, PilgrimGuideLink>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  progress: {
    current: number;
    total: number;
  };
  fetchLinks: (guideId?: string, pageSize?: number) => Promise<void>;
  getPilgrimDetails: (identifier: string, type: 'passport' | 'email', translations: any) => Promise<any>;
  linkPilgrimToGuide: (
    pilgrimId: string,
    guideId: string,
    organizerId: string,
    translations: any,
    isOwner?: boolean
  ) => Promise<void>;
  linkWaitingIdentification: (
    passportNumber: string,
    guideId: string,
    organizerId: string,
    translations: any,
    isOwner?: boolean
  ) => Promise<void>;
  deleteLink: (linkId: string, translations: any) => Promise<void>;
  deleteSelectedLinks: (translations: any) => Promise<void>;
  toggleLinkSelection: (linkId: string) => void;
  selectAllLinks: () => void;
  clearLinkSelection: () => void;
  selectedLinks: string[];
  refreshData: () => Promise<void>;
  toggleOwnership: (linkId: string) => Promise<void>;
};

export const usePilgrimGuideStore = create<PilgrimGuideState>((set, get) => {
  // Shared cache objects to reduce Firestore reads
  const pilgrims = new Map<string, any>();
  const guides = new Map<string, any>();
  const organizers = new Map<string, any>();

  // Helper function to process document IDs in chunks (Firebase "in" operator has a limit of 30)
  async function* chunkedQuery(collectionName: string, fieldPath: string, operator: any, values: string[]) {
    for (let i = 0; i < values.length; i += FIREBASE_IN_LIMIT) {
      const chunk = values.slice(i, i + FIREBASE_IN_LIMIT);
      const q = query(collection(firestore, collectionName), where(fieldPath, operator, chunk));
      const snapshot = await getDocs(q);
      yield snapshot.docs;
    }
  }

  /**
   * Process link data and build complete PilgrimGuideLink objects
   * @param linkDocs Array of link document snapshots
   */
  const processLinkDocs = async (linkDocs: any[]) => {
    console.log(`🔄 Processing ${linkDocs.length} link documents`);

    // Step 1: Extract all IDs we need to fetch
    const pilgrimIds = new Set<string>();
    const guideIds = new Set<string>();
    const organizerIds = new Set<string>();

    linkDocs.forEach(doc => {
      const data = doc.data();
      if (data.pilgrimId) pilgrimIds.add(data.pilgrimId);
      if (data.guideId) guideIds.add(data.guideId);
      if (data.organizerId) organizerIds.add(data.organizerId);
    });

    console.log(`📊 Unique IDs to fetch: ${pilgrimIds.size} pilgrims, ${guideIds.size} guides, ${organizerIds.size} organizers`);

    // Step 2: Fetch all required data in parallel with chunking for large sets
    const pilgrimPromises = [];
    const guidePromises = [];
    const organizerPromises = [];

    // Process pilgrim IDs in chunks
    for (let i = 0; i < Array.from(pilgrimIds).length; i += FIREBASE_IN_LIMIT) {
      const chunk = Array.from(pilgrimIds).slice(i, i + FIREBASE_IN_LIMIT);
      const q = query(collection(firestore, 'Pilgrims'), where(documentId(), 'in', chunk));
      pilgrimPromises.push(getDocs(q));
    }

    // Process guide IDs in chunks
    for (let i = 0; i < Array.from(guideIds).length; i += FIREBASE_IN_LIMIT) {
      const chunk = Array.from(guideIds).slice(i, i + FIREBASE_IN_LIMIT);
      const q = query(collection(firestore, 'Guides'), where(documentId(), 'in', chunk));
      guidePromises.push(getDocs(q));
    }

    // Process organizer IDs in chunks
    for (let i = 0; i < Array.from(organizerIds).length; i += FIREBASE_IN_LIMIT) {
      const chunk = Array.from(organizerIds).slice(i, i + FIREBASE_IN_LIMIT);
      const q = query(collection(firestore, 'Organizers'), where(documentId(), 'in', chunk));
      organizerPromises.push(getDocs(q));
    }

    // Fetch all data in parallel
    const [pilgrimSnapshots, guideSnapshots, organizerSnapshots] = await Promise.all([
      Promise.all(pilgrimPromises),
      Promise.all(guidePromises),
      Promise.all(organizerPromises)
    ]);

    // Process results and populate caches
    pilgrimSnapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        pilgrims.set(doc.id, doc.data());
      });
    });

    guideSnapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        guides.set(doc.id, doc.data());
      });
    });

    organizerSnapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        organizers.set(doc.id, doc.data());
      });
    });

    console.log(`✅ Successfully fetched related data: ${pilgrims.size} pilgrims, ${guides.size} guides, ${organizers.size} organizers`);

    // Step 3: Process links
    const processedLinks: Record<string, PilgrimGuideLink> = {};
    let completedLinks = 0;
    let incompletedLinks = 0;

    linkDocs.forEach(doc => {
      try {
        const linkData = doc.data();
        const linkId = doc.id;

        // Skip if we're missing critical IDs
        if (!linkData.pilgrimId || !linkData.guideId || !linkData.organizerId) {
          incompletedLinks++;
          return;
        }

        // Get related data from our caches
        const pilgrimData = pilgrims.get(linkData.pilgrimId);
        const guideData = guides.get(linkData.guideId);
        const organizerData = organizers.get(linkData.organizerId);

        // Skip if we're missing guide/organizer data
        if (!guideData || !organizerData) {
          incompletedLinks++;
          return;
        }

        if (!pilgrimData) {
          if (linkData.status === 'waiting_identification') {
            processedLinks[linkId] = {
              id: linkId,
              ...linkData,
              pilgrimName: linkData.pilgrimName ?? 'Waiting Identification',
              passportNumber: linkData.passportNumber ?? 'Unknown',
              gender: (linkData.gender ?? 'unknown') as 'male' | 'female' | 'unknown',
              residenceCountry: linkData.residenceCountry ?? null,
              packageName: linkData.packageName ?? 'Unknown Package',
              pilgrimType: linkData.pilgrimType ?? 'Unknown',
              nusukGroup: linkData.nusukGroup ?? null,
              guideName: guideData.name ?? linkData.guideName ?? 'Unknown Guide',
              organizerName: organizerData.name ?? linkData.organizerName ?? 'Unknown Organizer',
              mainMakkahRoomType: linkData.main_makkah_roomType ?? null,
              shiftingMakkahRoomType: linkData.shifting_makkah_roomType ?? null,
              madinahRoomType: linkData.madinah_roomType ?? null,
              age: linkData.age ?? null,
              nationalityCountry: linkData.nationalityCountry ?? null,
              mobileNumber: linkData.mobileNumber ?? null,
              email: linkData.email ?? null,
            } as PilgrimGuideLink;

            completedLinks++;
          } else {
            incompletedLinks++;
          }
          return;
        }

        processedLinks[linkId] = {
          id: linkId,
          ...linkData,
          pilgrimName: pilgrimData.Name,
          passportNumber: pilgrimData.Passport_Number,
          gender: pilgrimData.Gender?.toLowerCase() || 'unknown',
          residenceCountry: pilgrimData.Residence_Country ?? null,
          packageName: pilgrimData.package_nickName ?? 'Unknown Package',
          pilgrimType: pilgrimData.Pilgrim_Type ?? 'Unknown',
          nusukGroup: pilgrimData.nusuk_group ?? null,
          guideName: guideData.name,
          organizerName: organizerData.name,
          mainMakkahRoomType: pilgrimData.main_makkah_roomType ?? null,
          shiftingMakkahRoomType: pilgrimData.shifting_makkah_roomType ?? null,
          madinahRoomType: pilgrimData.madinah_roomType ?? null,
          age: pilgrimData.age ?? null,
          nationalityCountry: pilgrimData.Nationality_Country ?? null,
          mobileNumber: pilgrimData.Mobile_Number ?? null,
          email: pilgrimData.Email ?? null,
        } as PilgrimGuideLink;

        completedLinks++;
      } catch (error) {
        console.error(`❌ Error processing link ${doc.id}:`, error);
        incompletedLinks++;
      }
    });

    console.log(`📊 Link processing results: ${completedLinks} complete, ${incompletedLinks} incomplete`);
    return processedLinks;
  };

  return {
    links: {},
    loading: false,
    error: null,
    initialized: false,
    progress: {
      current: 0,
      total: 0
    },
    selectedLinks: [],

    fetchLinks: async (guideId?: string) => {
      // Skip if already initialized
      if (get().initialized && !guideId) {
        console.log(`Links already initialized, skipping fetch`);
        return;
      }

      console.log(`🚀 Starting optimized fetchLinks - ${new Date().toISOString()}`);
      set({ loading: true, error: null });

      try {
        const userRole = localStorage.getItem('userRole');
        const userId = localStorage.getItem('userId');

        console.log(`User role: ${userRole}, ID: ${userId}`);

        // 1. Get all relevant links based on user role
        let linkDocs: any[] = [];
        
        if (userRole === 'guide') {
          // For guides, only get their own links
          const linksSnapshot = await getDocs(query(
            collection(firestore, 'PilgrimGuideLinks'),
            where('guideId', '==', guideId || userId)
          ));
          linkDocs = linksSnapshot.docs;
        } 
        else if (userRole === 'organizer') {
          if (!userId) {
            set({ links: {}, loading: false, initialized: true });
            return;
          }

          const linksRef = collection(firestore, 'PilgrimGuideLinks');
          const organizerLinksSnapshot = await getDocs(query(
            linksRef,
            where('organizerId', '==', userId)
          ));
          linkDocs = organizerLinksSnapshot.docs;

          if (linkDocs.length === 0) {
            const guidesSnapshot = await getDocs(query(
              collection(firestore, 'Guides'),
              where('organizerId', '==', userId)
            ));

            const guideIds = guidesSnapshot.docs.map(doc => doc.id);
            if (guideIds.length > 0) {
              const linksPromises = [];
              for (let i = 0; i < guideIds.length; i += FIREBASE_IN_LIMIT) {
                const chunk = guideIds.slice(i, i + FIREBASE_IN_LIMIT);
                linksPromises.push(getDocs(query(
                  linksRef,
                  where('guideId', 'in', chunk)
                )));
              }
              const linkSnapshots = await Promise.all(linksPromises);
              linkDocs = linkSnapshots.flatMap(snapshot => snapshot.docs);
            }
          }
        } 
        else {
          // For admins, get all links
          const linksSnapshot = await getDocs(
            collection(firestore, 'PilgrimGuideLinks')
          );
          linkDocs = linksSnapshot.docs;
        }
        
        console.log(`📊 Fetched ${linkDocs.length} links`);
        
        // Set progress for UI
        set(state => ({
          ...state,
          progress: {
            current: 0,
            total: linkDocs.length
          }
        }));

        // 2. Process all links at once
        const processedLinks = await processLinkDocs(linkDocs);

        // 3. Update state with all processed links
        set({
          links: processedLinks,
          initialized: true,
          loading: false,
          progress: {
            current: Object.keys(processedLinks).length,
            total: Object.keys(processedLinks).length
          }
        });

        console.log(`✅ Fetch completed at ${new Date().toISOString()} with ${Object.keys(processedLinks).length} links`);
      } catch (error: any) {
        console.error(`❌ Error in fetchLinks:`, error);
        set({ error: error.message, loading: false });
      }
    },

    getPilgrimDetails: async (identifier: string, type: 'passport' | 'email', translations: any) => {
      try {
        console.log(`🔍 Getting pilgrim details with ${type}: ${identifier}`);
        let querySnapshot;
        let searchIdentifier;
        
        if (type === 'passport') {
          searchIdentifier = normalizePassportNumber(identifier).toUpperCase();
          console.log(`🔍 Normalized passport: ${searchIdentifier}`);
          
          console.log(`⏳ Executing passport queries...`);
          const [stringResults, numberResults] = await Promise.all([
            getDocs(query(
              collection(firestore, 'Pilgrims'),
              where('Passport_Number', 'in', [
                searchIdentifier,
                searchIdentifier.toLowerCase(),
                'PASSPORT' + searchIdentifier,
                'Passport' + searchIdentifier,
                'passport' + searchIdentifier
              ])
            )),
            getDocs(query(
              collection(firestore, 'Pilgrims'),
              where('Passport_Number', '==', parseInt(searchIdentifier))
            ))
          ]);

          console.log(`📊 Passport query results: string=${stringResults.size}, number=${numberResults.size}`);
          querySnapshot = {
            docs: [...stringResults.docs, ...numberResults.docs]
          };
        } else {
          searchIdentifier = String(identifier || '').trim();
          const upperEmail = searchIdentifier.toUpperCase();
          
          console.log(`🔍 Searching for email: ${searchIdentifier} (also trying ${upperEmail})`);
          console.log(`⏳ Executing email query...`);
          
          querySnapshot = await getDocs(query(
            collection(firestore, 'Pilgrims'),
            where('Email', 'in', [upperEmail, searchIdentifier])
          ));
          
          console.log(`📊 Email query results: ${querySnapshot.docs.length} documents`);
        }

        if (querySnapshot.docs.length === 0) {
          console.log(`❌ No pilgrim found with ${type}: ${searchIdentifier}`);
          const error = new Error(
            type === 'passport'
              ? `${translations?.pilgrimGuideStore?.errors?.pilgrimNotFoundPassport || 'No pilgrim found with passport number'}: ${searchIdentifier}`
              : `${translations?.pilgrimGuideStore?.errors?.pilgrimNotFoundEmail || 'No pilgrim found with email'}: ${searchIdentifier}`
          ) as Error & { code?: string };
          error.code = 'PILGRIM_NOT_FOUND';
          throw error;
        }

        const pilgrimDoc = querySnapshot.docs[0];
        const pilgrimData = pilgrimDoc.data();

        console.log(`✅ Found pilgrim: ${pilgrimData.Name}, ID: ${pilgrimDoc.id}`);
        console.log(`📊 Pilgrim details:`, {
          name: pilgrimData.Name,
          passport: pilgrimData.Passport_Number,
          gender: pilgrimData.Gender,
          country: pilgrimData.Residence_Country,
          package: pilgrimData.package_nickName
        });

        return {
          pilgrimId: pilgrimDoc.id,
          pilgrimName: pilgrimData.Name,
          passportNumber: pilgrimData.Passport_Number,
          gender: pilgrimData.Gender.toLowerCase(),
          country: pilgrimData.Residence_Country,
          packageName: pilgrimData.package_nickName,
          pilgrimType: pilgrimData.Pilgrim_Type,
          nusukGroup: pilgrimData.nusuk_group,
          searchIdentifier,
          searchType: type
        };
      } catch (error: any) {
        console.error('❌ Error getting pilgrim details:', error);
        throw error;
      }
    },

    linkPilgrimToGuide: async (
      pilgrimId: string,
      guideId: string,
      organizerId: string,
      translations: any,
      isOwner: boolean = true 
    ) => {
      console.log(`🔄 Linking pilgrim ${pilgrimId} to guide ${guideId}${isOwner ? '' : ' (Not Owner)'}`);
      await linkPilgrimToGuideAction(
        pilgrimId,
        guideId,
        organizerId,
        set,
        translations,
        isOwner
      );
    },
    linkWaitingIdentification: async (
      passportNumber: string,
      guideId: string,
      organizerId: string,
      translations: any,
      isOwner: boolean = true
    ) => {
      console.log(`🔄 Creating waiting identification link for passport ${passportNumber}`);
      await linkWaitingIdentificationAction(
        passportNumber,
        guideId,
        organizerId,
        set,
        translations,
        isOwner
      );
    },

    deleteLink: async (linkId: string, translations: any) => {
      console.log(`🗑️ Deleting link ${linkId}`);
      await deleteLinkAction(linkId, set, translations);
    },

    deleteSelectedLinks: async (translations: any) => {
      const state = get();
      if (state.selectedLinks.length === 0) return;

      console.log(`🗑️ Deleting ${state.selectedLinks.length} selected links`);
      const batch = writeBatch(firestore);

      try {
        for (const linkId of state.selectedLinks) {
          const linkRef = doc(firestore, 'PilgrimGuideLinks', linkId);
          batch.delete(linkRef);
        }

        console.log(`⏳ Committing batch delete of ${state.selectedLinks.length} links...`);
        await batch.commit();

        const newLinks = { ...state.links };
        state.selectedLinks.forEach(linkId => {
          delete newLinks[linkId];
        });

        console.log(`✅ Successfully deleted ${state.selectedLinks.length} links`);
        set({ 
          links: newLinks, 
          selectedLinks: []
        });

        toast.success(translations?.success?.deleteLinks || 'Selected links deleted successfully');
      } catch (error: any) {
        console.error('❌ Error deleting selected links:', error);
        set({ error: error.message });
        throw error;
      }
    },

    toggleLinkSelection: (linkId: string) => {
      set(state => {
        const selectedLinks = [...state.selectedLinks];
        const index = selectedLinks.indexOf(linkId);
        
        if (index === -1) {
          selectedLinks.push(linkId);
        } else {
          selectedLinks.splice(index, 1);
        }
        
        return { selectedLinks };
      });
    },

    selectAllLinks: () => {
      set(state => ({
        selectedLinks: Object.keys(state.links)
      }));
    },

    clearLinkSelection: () => {
      set({ selectedLinks: [] });
    },

    refreshData: async () => {
      console.log(`🔄 Refreshing all pilgrim guide data...`);
      // Clear caches
      pilgrims.clear();
      guides.clear();
      organizers.clear();
      
      set({ 
        links: {},
        initialized: false,
        selectedLinks: [],
      });
      
      await get().fetchLinks();
      console.log(`✅ Data refresh completed`);
    },

    toggleOwnership: async (linkId: string) => {
      try {
        const link = get().links[linkId];
        if (!link) {
          throw new Error('Link not found');
        }

        console.log(`🔄 Toggling ownership for link ${linkId}, current value: ${link.isOwner === false ? 'Not Owner' : 'Owner'}`);
        const linkRef = doc(firestore, 'PilgrimGuideLinks', linkId);
        const newOwnerStatus = link.isOwner === false ? undefined : false;
        
        console.log(`⏳ Updating ownership to: ${newOwnerStatus === false ? 'Not Owner' : 'Owner'}`);
        await updateDoc(linkRef, {
          isOwner: newOwnerStatus,
          updatedAt: Date.now()
        });
        
        // Update local state
        set(state => ({
          links: {
            ...state.links,
            [linkId]: {
              ...state.links[linkId],
              isOwner: newOwnerStatus
            }
          }
        }));
        
        console.log(`✅ Ownership updated successfully`);
        toast.success(
          newOwnerStatus === false
            ? 'Pilgrim marked as Not Owner'
            : 'Pilgrim marked as Owner'
        );
      } catch (error: any) {
        console.error('❌ Error toggling ownership:', error);
        toast.error(error.message || 'Failed to update ownership status');
        throw error;
      }
    }
  };
});
